import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import type {
	AccountNotification,
	CurrentUser,
	Permissions,
	SortDirection,
} from '@/javascript/interfaces'
import type { ErrorType } from '@/javascript/interfaces'

import { PER_PAGE } from '../helpers/_constants'
import type { Seats } from '../services/administratorDashboard/interfaces'
import type { AgencyAdministrator, AgencyAdministratorPaths } from '../services/agencyAdministrators/interfaces'
import api from '../services/api'
import { getMembers, type SortBy } from '../services/members'
import type {
	FilterAndSort,
	Member,
	MemberFilter,
} from '../services/members/interfaces'
import { getAgencyId } from '../utils/getAgencyId'
import { useGlobalStore } from './GlobalStore'
import { useMembersStore } from './MembersStore'
import { useNotificationStore } from './NotificationStore'

export const useAdministratorStore = defineStore('administrator', () => {
	// State
	const accountNotifications = ref<AccountNotification[]>([])
	const administratorPaths = ref<AgencyAdministratorPaths>({
		create: '',
		delete: '',
		list: '',
		update: '',
		verify: '',
	})
	const allMembersSelected = ref(false)
	const annualDueDateSoonInDays = ref(0)
	const canModifyAdmins = ref(false)
	const canModifyBilling = ref(false)
	const currentUser = ref<CurrentUser | null>(null)
	const globalStore = useGlobalStore()
	const hideSelectAll = ref(false)
	const inactiveMembersUrl = ref('')
	const loading = ref(false)
	const memberSearchInput = ref('')
	const membersStore = useMembersStore()
	const modalHeading = ref('')
	const notificationStore = useNotificationStore()
	const pagination = ref<FilterAndSort>({
		currentPage: 1,
		filterBy: '' as MemberFilter,
		filterByQuery: '',
		filterGroupBy: null as number | null,
		perPage: PER_PAGE.DEFAULT,
		sortBy: '' as SortBy,
		sortDirection: 'ascending' as SortDirection,
	})
	const paginationTotal = ref<number>(0)
	const permissions = ref<Permissions>({ canModifyAdmins: false })
	const seats = ref<Seats>({
		max: 0,
		used: 0,
	})
	const selectedAdmin = ref<AgencyAdministrator>({
		can_modify_admins: false,
		can_modify_billing: false,
		email: '',
		first_name: '',
		id: null,
		last_name: '',
		phone: '',
		url_for_editing: '',
	})
	const selectedMembers = ref<Member[]>([])
	const selectedTab = ref('')
	const showInitialTrainingDueDate = ref(false)
	const softwareProviderName = ref('')

	// Getters
	const selectedAll = computed({
		get: () => {
			const memberList = membersStore.memberList

			if (!memberList.length) {
				return false
			}

			return selectedMembers.value.length === memberList.length
		},
		set: (value: boolean) => {
			if (value) {
				selectedMembers.value = membersStore.memberList
			} else {
				selectedMembers.value = []
			}
		},
	})

	const selectedTabMemberFilter = computed(() => {
		switch (selectedTab.value) {
			case 'initial-training':
				return 'initial training incomplete'

			case 'compliance-expires-soon':
				return 'annual training due soon'

			case 'compliance-expired':
				return 'overdue annual training'

			default:
				return ''
		}
	})

	// Actions
	const applyFilter = () => {
		pagination.value.currentPage = 1
		retrieveMembers()
	}

	const clearAdmin = () => {
		selectedAdmin.value = {
			can_modify_admins: false,
			can_modify_billing: false,
			email: '',
			first_name: '',
			id: null,
			last_name: '',
			phone: '',
			url_for_editing: '',
		}

		globalStore.resetCAModal()
	}

	const getSelectedMemberIds = (): number[] => {
		return selectedMembers.value.map(({ id }) => id)
	}

	const handlePageChange = (page: number) => {
		pagination.value.currentPage = page
		retrieveMembers()
	}

	const isSelectedMember = (value: Member): boolean => {
		return selectedMembers.value.includes(value)
	}

	const resetData = () => {
		selectedMembers.value = [] as Member[]
	}

	const retrieveAccountNotifications = async () => {
		// We are simply short circuiting this because account notifications is currently not returning any data right now.
		return
	}

	const retrieveMembers = async () => {
		try {
			loading.value = true

			if (getAgencyId()) {
				const result = await getMembers(api, getAgencyId(), {
					filter: pagination.value.filterBy,
					group_id: pagination.value.filterGroupBy,
					page: pagination.value.currentPage,
					per_page: pagination.value.perPage,
					query: pagination.value.filterByQuery,
					sort_by: pagination.value.sortBy,
					sort_direction: pagination.value.sortDirection,
				})

				membersStore.memberList = result['data']
				paginationTotal.value = result['pagination']['total']
				window.sessionStorage.setItem('filterBy', pagination.value.filterBy)
			}
		} catch (error) {
			notificationStore.onHandleErrorMessages({ error: error as ErrorType })
		} finally {
			loading.value = false
		}
	}

	const setActiveTab = (tabName: MemberFilter) => {
		selectedTab.value = tabName
		pagination.value.filterBy = tabName as MemberFilter
		pagination.value.currentPage = 1
	}

	const updateSelectedMembers = (value: Member, isSelected?: boolean) => {
		const selectedIndex = selectedMembers.value.findIndex(member => member.id === value.id)

		if (isSelected) {
			selectedMembers.value = [
				...selectedMembers.value,
				value,
			]
		} else {
			if (selectedIndex !== -1) {
				selectedMembers.value = selectedMembers.value.filter(member => member.id !== value.id)
			}
		}
	}

	return {
		// State
		accountNotifications,
		administratorPaths,
		allMembersSelected,
		annualDueDateSoonInDays,
		canModifyAdmins,
		canModifyBilling,
		currentUser,
		hideSelectAll,
		inactiveMembersUrl,
		loading,
		memberSearchInput,
		modalHeading,
		pagination,
		paginationTotal,
		permissions,
		seats,
		selectedAdmin,
		selectedMembers,
		selectedTab,
		showInitialTrainingDueDate,
		softwareProviderName,

		// Getters
		selectedAll,
		selectedTabMemberFilter,

		// Actions
		applyFilter,
		clearAdmin,
		getSelectedMemberIds,
		handlePageChange,
		isSelectedMember,
		resetData,
		retrieveAccountNotifications,
		retrieveMembers,
		setActiveTab,
		updateSelectedMembers,
	}
})
